import {FC} from "react";
import LineChartCard from "../../../LineChartCard";
import FileTextIcon from "../../../../assets/icons/file-text.svg";
import MailIcon from "../../../../assets/icons/mail-icon.svg";
import DollarIcon from "../../../../assets/icons/dollar-icon.svg";
import OpenedBookIcon from "../../../../assets/icons/opened-book.svg";
import {IMetricsData} from "../../../../pages/Dashboard/Dashboard.type";

type ChartCategory = "patents" | "news" | "science" | "investment";

type ChartId = `chart-${ChartCategory}`;

interface DataPoint {
  readonly year: number;
  readonly value: number;
}

interface ChartDataPoint {
  readonly год: number;
  readonly вМире: number;
  readonly вРоссии: number;
}

type CategoryData = {
  readonly world: readonly DataPoint[];
  readonly russia: readonly DataPoint[];
};

type MarketScienceData = Record<ChartCategory, CategoryData>;

type ChartColor = `#${string}`;
type GradientBackground = `linear-gradient(${string})`;

interface BaseChartConfig {
  readonly id: ChartId;
  readonly title: string;
  readonly icon: string;
  readonly firstLineColor: ChartColor;
  readonly secondLineColor: ChartColor;
  readonly bgColor: GradientBackground | ChartColor;
  readonly borderColor: ChartColor;
  readonly tooltipColor: ChartColor;
}

type ChartConfig = BaseChartConfig & {
  readonly data: ChartDataPoint[];
};

const CHART_BORDER_COLORS = {
  BLUE: "#396C96",
  PURPLE: "#7B3996",
  YELLOW: "#DEB52F",
  GREEN: "#399639",
} as const satisfies Record<string, ChartColor>;

const CHART_COLORS = {
  BLUE: "#3B82F6",
  PURPLE: "#8B45FF",
  GREEN: "#10B981",
  ORANGE: "#F59E0B",
  RED: "#EF4444",
  YELLOW: "#FFCE2F",
  WHITE: "#FFFFFF",
} as const satisfies Record<string, ChartColor>;

const GRADIENT_BACKGROUNDS = {
  PURPLE: "linear-gradient(135deg, #2D1B4E 0%, #1A0B2E 100%)",
  GREEN: "#39963933",
  LIGHT_BLUE: "#396C9633",
  YELLOW: "#DEB52F33",
} as const satisfies Record<string, GradientBackground | string>;

const CHART_TITLES: Record<ChartCategory, string> = {
  patents: "Патенты",
  news: "Новости",
  science: "Научные публикации",
  investment: "Инвестиции",
} as const;

const CHART_ICONS: Record<ChartCategory, string> = {
  patents: FileTextIcon,
  news: MailIcon,
  science: OpenedBookIcon,
  investment: DollarIcon,
} as const;

const CHART_TOOLTIP_COLORS = {
  BLUE: "#396C96E6",
  PURPLE: "#7B3996E6",
  YELLOW: "#DEB52FB3",
  GREEN: "#399639E6",
} as const satisfies Record<string, ChartColor>;

const CHART_STYLE_CONFIG: Record<
  ChartCategory,
  Omit<BaseChartConfig, "id" | "title" | "icon">
> = {
  patents: {
    firstLineColor: CHART_COLORS.WHITE,
    secondLineColor: CHART_COLORS.BLUE,
    bgColor: GRADIENT_BACKGROUNDS.LIGHT_BLUE,
    borderColor: CHART_BORDER_COLORS.BLUE,
    tooltipColor: CHART_TOOLTIP_COLORS.BLUE,
  },
  news: {
    firstLineColor: CHART_COLORS.WHITE,
    secondLineColor: CHART_COLORS.BLUE,
    bgColor: GRADIENT_BACKGROUNDS.YELLOW,
    borderColor: CHART_BORDER_COLORS.YELLOW,
    tooltipColor: CHART_TOOLTIP_COLORS.YELLOW,
  },
  science: {
    firstLineColor: CHART_COLORS.WHITE,
    secondLineColor: CHART_COLORS.BLUE,
    bgColor: GRADIENT_BACKGROUNDS.PURPLE,
    borderColor: CHART_BORDER_COLORS.PURPLE,
    tooltipColor: CHART_TOOLTIP_COLORS.PURPLE,
  },

  investment: {
    firstLineColor: CHART_COLORS.WHITE,
    secondLineColor: CHART_COLORS.BLUE,
    bgColor: GRADIENT_BACKGROUNDS.GREEN,
    borderColor: CHART_BORDER_COLORS.GREEN,
    tooltipColor: CHART_TOOLTIP_COLORS.GREEN,
  },
} as const;

const MOCK_DATA: MarketScienceData = {
  patents: {
    world: [
      {year: 2000, value: 100},
      {year: 2005, value: 150},
      {year: 2010, value: 220},
      {year: 2015, value: 310},
      {year: 2020, value: 450},
    ],
    russia: [
      {year: 2000, value: 30},
      {year: 2005, value: 45},
      {year: 2010, value: 70},
      {year: 2015, value: 90},
      {year: 2020, value: 120},
    ],
  },
  science: {
    world: [
      {year: 2000, value: 1200},
      {year: 2005, value: 1500},
      {year: 2010, value: 2000},
      {year: 2015, value: 2700},
      {year: 2020, value: 3500},
    ],
    russia: [
      {year: 2000, value: 300},
      {year: 2005, value: 400},
      {year: 2010, value: 500},
      {year: 2015, value: 600},
      {year: 2020, value: 750},
    ],
  },
  news: {
    world: [
      {year: 2000, value: 500},
      {year: 2005, value: 650},
      {year: 2010, value: 800},
      {year: 2015, value: 950},
      {year: 2020, value: 1200},
    ],
    russia: [
      {year: 2000, value: 120},
      {year: 2005, value: 180},
      {year: 2010, value: 250},
      {year: 2015, value: 320},
      {year: 2020, value: 400},
    ],
  },

  investment: {
    world: [
      {year: 2000, value: 50},
      {year: 2005, value: 75},
      {year: 2010, value: 110},
      {year: 2015, value: 160},
      {year: 2020, value: 220},
    ],
    russia: [
      {year: 2000, value: 10},
      {year: 2005, value: 20},
      {year: 2010, value: 35},
      {year: 2015, value: 50},
      {year: 2020, value: 70},
    ],
  },
} as const;

/** Last year shown on market/science charts; CMS may contain later years. */
export const MARKET_SCIENCE_MAX_DISPLAY_YEAR = 2025;

const normalizeMetricYear = (year: unknown): number | null => {
  const normalizedYear = Number(year);
  return Number.isFinite(normalizedYear) ? normalizedYear : null;
};

const isSameMetricYear = (left: unknown, right: number): boolean =>
  normalizeMetricYear(left) === right;

const transformDataForChart = <T extends readonly DataPoint[]>(
  worldData: T,
  russiaData: T
): ChartDataPoint[] => {
  const uniqueYears = Array.from(
    new Set(
      worldData
        .map(({year}) => normalizeMetricYear(year))
        .filter(
          (year): year is number =>
            year !== null && year <= MARKET_SCIENCE_MAX_DISPLAY_YEAR
        )
    )
  ).sort((a, b) => a - b);

  return uniqueYears.map((year): ChartDataPoint => {
    const worldValue =
      worldData.find(({year: y}) => isSameMetricYear(y, year))?.value ?? 0;
    const russiaValue =
      russiaData.find(({year: y}) => isSameMetricYear(y, year))?.value ?? 0;

    return {
      год: year,
      вМире: worldValue,
      вРоссии: russiaValue,
    } as const;
  });
};

const createChartConfig = <T extends ChartCategory>(
  category: T,
  data: MarketScienceData[T]
): ChartConfig => ({
  id: `chart-${category}` as ChartId,
  title: CHART_TITLES[category],
  icon: CHART_ICONS[category],
  data: transformDataForChart(data.world, data.russia),
  ...CHART_STYLE_CONFIG[category],
});

const createAllChartConfigurations = (
  data: MarketScienceData
): ChartConfig[] => {
  return (Object.keys(data) as ChartCategory[]).map((category) =>
    createChartConfig(category, data[category])
  );
};

const METRIC_CATEGORIES: ChartCategory[] = [
  "patents",
  "news",
  "science",
  "investment",
];

const getMetricsChartYears = (dataCharts?: IMetricsData): number[] => {
  const years = new Set<number>();

  for (const category of METRIC_CATEGORIES) {
    dataCharts?.[category]?.world?.forEach(({year}) => {
      const normalizedYear = normalizeMetricYear(year);
      if (
        normalizedYear !== null &&
        normalizedYear <= MARKET_SCIENCE_MAX_DISPLAY_YEAR
      ) {
        years.add(normalizedYear);
      }
    });
    dataCharts?.[category]?.russia?.forEach(({year}) => {
      const normalizedYear = normalizeMetricYear(year);
      if (
        normalizedYear !== null &&
        normalizedYear <= MARKET_SCIENCE_MAX_DISPLAY_YEAR
      ) {
        years.add(normalizedYear);
      }
    });
  }

  if (!years.size) {
    return Array.from(
      {length: MARKET_SCIENCE_MAX_DISPLAY_YEAR - 2009 + 1},
      (_, index) => 2009 + index
    );
  }

  const sortedYears = Array.from(years).sort((a, b) => a - b);
  const minYear = sortedYears[0];
  const maxYear = Math.min(
    sortedYears[sortedYears.length - 1],
    MARKET_SCIENCE_MAX_DISPLAY_YEAR
  );

  return Array.from(
    {length: maxYear - minYear + 1},
    (_, index) => minYear + index
  );
};

export const MarketScienceChartPanel: FC<{dataCharts?: IMetricsData}> = ({
  dataCharts,
}) => {
  const years = getMetricsChartYears(dataCharts);

  const marketScienceData: MarketScienceData = {
    patents: {
      world: years.map((year) => ({
        year,
        value:
          dataCharts?.patents?.world?.find(({year: y}) =>
            isSameMetricYear(y, year)
          )?.value ?? 0,
      })),
      russia: years.map((year) => ({
        year,
        value:
          dataCharts?.patents?.russia?.find(({year: y}) =>
            isSameMetricYear(y, year)
          )?.value ?? 0,
      })),
    },
    science: {
      world: years.map((year) => ({
        year,
        value:
          dataCharts?.science?.world?.find(({year: y}) =>
            isSameMetricYear(y, year)
          )?.value ?? 0,
      })),
      russia: years.map((year) => ({
        year,
        value:
          dataCharts?.science?.russia?.find(({year: y}) =>
            isSameMetricYear(y, year)
          )?.value ?? 0,
      })),
    },
    news: {
      world: years.map((year) => ({
        year,
        value:
          dataCharts?.news?.world?.find(({year: y}) =>
            isSameMetricYear(y, year)
          )?.value ?? 0,
      })),
      russia: years.map((year) => ({
        year,
        value:
          dataCharts?.news?.russia?.find(({year: y}) =>
            isSameMetricYear(y, year)
          )?.value ?? 0,
      })),
    },
    investment: {
      world: years.map((year) => ({
        year,
        value:
          dataCharts?.investment?.world?.find(({year: y}) =>
            isSameMetricYear(y, year)
          )?.value ?? 0,
      })),
      russia: years.map((year) => ({
        year,
        value:
          dataCharts?.investment?.russia?.find(({year: y}) =>
            isSameMetricYear(y, year)
          )?.value ?? 0,
      })),
    },
  } as const;
  const chartConfigurations = createAllChartConfigurations(marketScienceData);

  const containerStyles = {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    // padding: "20px",
  } as const satisfies React.CSSProperties;

  return (
    <div style={containerStyles}>
      {chartConfigurations.map(({id, ...config}) => (
        <LineChartCard
          key={id}
          category={id}
          maxXYear={MARKET_SCIENCE_MAX_DISPLAY_YEAR}
          {...config}
        />
      ))}
    </div>
  );
};
